import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PublicShell } from "@/components/PublicShell";
import { toast } from "sonner";

/** Step one of an employment application: who you are, so we can email you a private link back to it. */
export default function ApplyStart() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const isPool = pid === "interest";
  const [position, setPosition] = useState(null);
  const [form, setForm] = useState({ legal_first: "", legal_last: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isPool) return;
    api.get(`/careers/postings/${pid}`).then(({ data }) => setPosition(data.position))
      .catch(() => setError("That position is not posted."));
  }, [pid, isPool]);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/careers/applications", {
        position_id: isPool ? "" : pid, ...form });
      toast.success(data.resumed ? "We reopened your saved application" : "Application started — check your email for your private link");
      navigate(`/careers/application/${data.token}`, { replace: true });
    } catch (e2) {
      setError(apiError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <PublicShell testid="apply-start-page">
      <Link to={isPool ? "/careers" : `/careers/${pid}`} className="text-sm font-bold text-[#3B82F6]">Back</Link>
      <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6]">
        {isPool ? "Future opportunities" : "Employment application"}
      </p>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
        {isPool ? "Join our future-opportunities pool" : position?.title || "Apply"}
      </h1>
      <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed text-sm">
        {isPool
          ? "This is a general expression of interest, not an application for a current opening. We will contact you if a suitable role opens."
          : "This is an application for employment. It is separate from enrolling a child, and it never creates a parent or staff account."}
      </p>

      <form onSubmit={start} className="mt-8 bg-white rounded-2xl border border-border p-6 sm:p-8 max-w-xl space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-bold">Legal first name</Label>
            <Input required className="mt-2" value={form.legal_first} data-testid="start-first-name"
                   onChange={(e) => setForm({ ...form, legal_first: e.target.value })} />
          </div>
          <div>
            <Label className="font-bold">Legal last name</Label>
            <Input required className="mt-2" value={form.legal_last} data-testid="start-last-name"
                   onChange={(e) => setForm({ ...form, legal_last: e.target.value })} />
          </div>
        </div>
        <div>
          <Label className="font-bold">Email address</Label>
          <Input required type="email" className="mt-2" value={form.email} data-testid="start-email"
                 onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <p className="mt-2 text-xs text-muted-foreground">
            We email a private link so you can save your answers and come back on any device.
          </p>
        </div>
        {error && <p className="text-sm font-semibold text-[#EF4444] bg-red-50 rounded-xl p-4" data-testid="start-error">{error}</p>}
        <Button type="submit" disabled={busy} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold w-full h-12"
                data-testid="start-application-btn">
          {busy ? "Starting…" : isPool ? "Start my expression of interest" : "Start my application"}
        </Button>
      </form>
    </PublicShell>
  );
}
